import { Bell, CaretRight, List } from '@phosphor-icons/react';
import { Link, useLocation, useMatch } from 'react-router-dom';
import { Button } from '@/components/ui-kit/button';
import { Menubar, MenubarMenu, MenubarTrigger } from '@/components/ui-kit/menubar';
import { useSidebar } from '@/components/ui-kit/sidebar';
import { Notification } from '@/components/core/notification/component/notification/notification';
import { ProfileMenu } from '@/components/core/profile-menu/profile-menu';
import { cn } from '@/lib/utils';
import { useGetContactDetails } from '@/modules/contacts/hooks/use-contacts';
import { getContactDisplayName } from '@/modules/contacts/utils/contact-attributes';
import { useGetListById } from '@/modules/lists/hooks/use-lists';
import { getCampaignReport } from '@/modules/reports/services/reports.service';
import {
  getCampaignShellBreadcrumbs,
  type CampaignBreadcrumb,
} from './campaign-shell-breadcrumbs';

type NotificationsData = {
  notifications: unknown[];
  unReadNotificationsCount: number;
  totalNotificationsCount: number;
};

type CampaignTopBarProps = {
  notifications: NotificationsData;
};

const CampaignBreadcrumbs = ({ crumbs }: { crumbs: CampaignBreadcrumb[] }) => {
  if (crumbs.length === 0) {
    return null;
  }

  return (
    <nav
      aria-label="Breadcrumb"
      className="flex min-w-0 items-center gap-2 text-[13px] font-medium leading-[18px] text-neutral-600"
    >
      {crumbs.map((crumb, index) => {
        const isLast = index === crumbs.length - 1;

        return (
          <span key={`${crumb.label}-${index}`} className="flex min-w-0 items-center gap-2">
            {index > 0 ? (
              <CaretRight className="size-3 shrink-0 text-neutral-400" aria-hidden />
            ) : null}
            {crumb.href && !isLast ? (
              <Link to={crumb.href} className="hover:text-neutral-900">
                {crumb.label}
              </Link>
            ) : (
              <span className={cn(isLast && 'truncate text-neutral-900')}>{crumb.label}</span>
            )}
          </span>
        );
      })}
    </nav>
  );
};

export const CampaignTopBar = ({ notifications }: CampaignTopBarProps) => {
  const { pathname } = useLocation();
  const { isMobile, toggleSidebar } = useSidebar();
  const contactMatch = useMatch('/contacts/:contactId');
  const listMatch = useMatch('/lists/:listId');
  const reportMatch = useMatch('/reports/campaigns/:campaignId/*');
  const reportMatchExact = useMatch('/reports/campaigns/:campaignId');
  const reportCampaignId =
    reportMatch?.params.campaignId || reportMatchExact?.params.campaignId;
  const reportCampaign = reportCampaignId ? getCampaignReport(reportCampaignId) : null;
  const contactId =
    contactMatch?.params.contactId && contactMatch.params.contactId !== 'import'
      ? contactMatch.params.contactId
      : undefined;
  const listId = listMatch?.params.listId
    ? decodeURIComponent(listMatch.params.listId)
    : undefined;
  const { contact } = useGetContactDetails(contactId ?? '');
  const { data: list } = useGetListById(listId ?? '', { enabled: Boolean(listId) });

  const crumbs = getCampaignShellBreadcrumbs(pathname);
  const contactName = contact ? getContactDisplayName(contact) : undefined;
  const listName = list?.name?.trim();
  const reportName = reportCampaign?.name?.trim();

  const breadcrumbItems =
    reportCampaignId && reportName
      ? crumbs.map((c) =>
          c.label === 'Campaign'
            ? {
                /* Prototype ReportNav.crumbs: middle crumb is the campaign name
                   and links to Performance when a leaf tab is active. */
                label: reportName,
                href: c.href,
              }
            : c
        )
      : contactId && contactName
      ? [...crumbs, { label: contactName }]
      : contactId
        ? [...crumbs, { label: 'Contact' }]
        : listId && listName
          ? [...crumbs, { label: listName }]
          : listId
            ? [...crumbs, { label: 'List' }]
            : crumbs;

  return (
    <header
      data-testid="campaign-top-bar"
      className="z-20 flex h-14 shrink-0 items-center gap-3 border-b border-neutral-200 bg-white px-4 sm:px-6"
    >
      {isMobile ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0 rounded-md text-neutral-600 hover:bg-neutral-100 lg:hidden"
          onClick={toggleSidebar}
          aria-label="Open menu"
        >
          <List className="size-5" />
        </Button>
      ) : null}

      <CampaignBreadcrumbs crumbs={breadcrumbItems} />

      <div className="flex-1" />

      <Menubar className="border-none p-0">
        <MenubarMenu>
          <MenubarTrigger
            asChild
            className="cursor-pointer p-0 focus:bg-transparent data-[state=open]:bg-transparent"
          >
            <div className="relative">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-md text-neutral-600 hover:bg-neutral-100"
                aria-label="Notifications"
              >
                <Bell className="size-[18px]" />
              </Button>
              {notifications.unReadNotificationsCount > 0 ? (
                <span className="absolute right-2 top-2 size-2 rounded-full bg-error" />
              ) : null}
            </div>
          </MenubarTrigger>
          <Notification />
        </MenubarMenu>
      </Menubar>

      <ProfileMenu variant="compact" />
    </header>
  );
};
